import React, { useRef, useState } from 'react';
import { FileText, Upload, X } from 'lucide-react';
import { putFile, fmtMB } from '../utils/issueVideo';
import { VideoProgress } from './IssueVideos';

// PDF documents on the Issue Register — an MRS redirection report, AP's email printed
// to PDF, a building manager's letter. OFFICE-ONLY: driver-issues-sb never sends them to
// the portal (an MRS report names other customers). Same direct-to-bucket upload as
// videos, so a big PDF never goes through the 6MB function body.

export const MAX_ISSUE_DOCS = 10;
export const MAX_DOC_BYTES = 20 * 1024 * 1024;

const isPdf = (f) => f && (f.type === 'application/pdf' || /\.pdf$/i.test(f.name || ''));

export function checkDoc(file) {
  if (!isPdf(file)) return `${(file && file.name) || 'That file'} is not a PDF.`;
  if (file.size > MAX_DOC_BYTES) return `${file.name} is ${fmtMB(file.size)} — the limit is 20MB.`;
  return '';
}

// api(action, payload) → JSON. Resolves to the updated (signed) issue.
export async function uploadIssueDoc(api, issueId, file, onProgress) {
  const bad = checkDoc(file);
  if (bad) throw new Error(bad);
  const start = await api('docUploadUrl', { id: issueId, type: 'application/pdf', size: file.size, name: file.name });
  if (!start || !start.success) throw new Error((start && start.error) || 'Could not start the upload.');
  await putFile(start.uploadUrl, file, 'application/pdf', onProgress);
  const done = await api('attachDoc', { id: issueId, path: start.path, size: file.size, name: file.name });
  if (!done || !done.success) throw new Error((done && done.error) || 'The PDF uploaded but could not be saved — try again.');
  return done.issue;
}

// Upload PDFs held on the new-issue form once the issue exists. Returns the ones that
// did NOT make it, so the form can stay open with them for a retry.
export async function uploadPendingDocs(api, issueId, files, onStatus) {
  const left = [];
  let lastErr = '';
  for (let i = 0; i < files.length; i++) {
    try {
      await uploadIssueDoc(api, issueId, files[i], (f) => onStatus && onStatus(`Uploading PDF ${i + 1} of ${files.length}… ${Math.round(f * 100)}%`, f));
    } catch (e) { left.push(files[i]); lastErr = e.message || 'Upload failed'; }
  }
  return { left, error: lastErr };
}

const fileRow = { display: 'flex', alignItems: 'center', gap: 6, fontSize: '.82rem', color: 'var(--hp-text)' };
const xBtn = { background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', padding: 2 };

function PdfPicker({ onFiles, disabled, label }) {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept="application/pdf,.pdf" multiple style={{ display: 'none' }}
        onChange={(e) => { const fs = Array.from(e.target.files || []); e.target.value = ''; if (fs.length) onFiles(fs); }} />
      <button type="button" className="btn btn-secondary btn-sm" disabled={disabled} onClick={() => ref.current && ref.current.click()}>
        <Upload size={13} /> {label}
      </button>
    </>
  );
}

// The PDFs on an issue, each opening in a new tab, plus "Add a PDF". Remove takes it
// off the issue (so it stops going to AP); the server keeps the file and a record.
export function IssueDocs({ api, issue, onChange, notify }) {
  const [status, setStatus] = useState(null);   // null | { text, frac }
  const docs = issue.documents || [];

  const add = async (files) => {
    const room = MAX_ISSUE_DOCS - docs.length;
    let latest = null;
    for (let i = 0; i < Math.min(files.length, room); i++) {
      const f = files[i];
      setStatus({ text: `Uploading ${f.name}…`, frac: 0 });
      try {
        latest = await uploadIssueDoc(api, issue.id, f, (frac) => setStatus({ text: `Uploading ${f.name}… ${Math.round(frac * 100)}%`, frac }));
        onChange(latest);
      } catch (e) { notify?.(e.message || 'Upload failed', 'error'); }
    }
    if (files.length > room) notify?.(`Only ${MAX_ISSUE_DOCS} PDFs per issue — ${files.length - room} not added.`, 'error');
    setStatus(null);
  };

  const remove = async (d) => {
    if (!window.confirm(`Take ${d.name} off this issue? It will no longer be sent to AP.`)) return;
    const r = await api('removeDoc', { id: issue.id, path: d.path });
    if (r.success) onChange(r.issue);
    else notify?.(r.error || 'Could not remove the PDF', 'error');
  };

  return (
    <div style={{ marginBottom: 12 }}>
      {docs.length > 0 && (
        <>
          <div style={{ display: 'block', fontSize: '.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--hp-dim)', marginBottom: 6 }}>
            Documents ({docs.length}) — office only, not shown to the driver
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
            {docs.map((d) => (
              <div key={d.path} style={fileRow}>
                <FileText size={14} style={{ color: 'var(--hp-gold)', flexShrink: 0 }} />
                <a href={d.url} target="_blank" rel="noreferrer" style={{ color: 'var(--hp-gold)', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</a>
                <span style={{ color: 'var(--hp-dim)', fontSize: '.72rem', whiteSpace: 'nowrap' }}>{d.size ? fmtMB(d.size) : ''}{d.by ? ` · ${d.by}` : ''}</span>
                <button type="button" aria-label={`Remove ${d.name}`} title="Remove from this issue" style={xBtn} onClick={() => remove(d)}><X size={14} /></button>
              </div>
            ))}
          </div>
        </>
      )}
      {docs.length < MAX_ISSUE_DOCS && <PdfPicker onFiles={add} disabled={!!status} label={docs.length ? 'Add another PDF' : 'Add a PDF'} />}
      {status && <VideoProgress text={status.text} frac={status.frac} />}
    </div>
  );
}

// New-issue form: PDFs are held here and uploaded once the issue is saved.
export function PendingDocPicker({ files, setFiles }) {
  const [error, setError] = useState('');
  const add = (fs) => {
    const bad = fs.map(checkDoc).filter(Boolean);
    setError(bad.join(' '));
    setFiles((cur) => [...cur, ...fs.filter((f) => !checkDoc(f))].slice(0, MAX_ISSUE_DOCS));
  };
  return (
    <div>
      {files.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 6 }}>
          {files.map((f, i) => (
            <div key={i} style={fileRow}>
              <FileText size={13} style={{ color: 'var(--hp-gold)' }} />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name} · {fmtMB(f.size)}</span>
              <button type="button" aria-label="Remove PDF" style={xBtn} onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))}><X size={14} /></button>
            </div>
          ))}
        </div>
      )}
      {files.length < MAX_ISSUE_DOCS && <PdfPicker onFiles={add} label={files.length ? 'Add another PDF' : 'Add a PDF'} />}
      {error && <div style={{ color: '#ef4444', fontSize: '0.78rem', marginTop: 6 }}>{error}</div>}
    </div>
  );
}
